import { RouteObject, Navigate } from 'react-router-dom';
import { lazyWithRetry } from '@/utils/lazyWithRetry';

// Lazy-loaded public pages with retry for network reliability
const SmartRedirect = lazyWithRetry(() => import('@/pages/SmartRedirect'));
const OrganizationSettings = lazyWithRetry(() => import('@/pages/OrganizationSettings'));
const AcceptOrganizationInvite = lazyWithRetry(() => import('@/pages/AcceptOrganizationInvite'));
const Index = lazyWithRetry(() => import('@/pages/Index'));
const About = lazyWithRetry(() => import('@/pages/About'));
const Blog = lazyWithRetry(() => import('@/pages/Blog'));
const BlogCategory = lazyWithRetry(() => import('@/pages/BlogCategory'));
const BlogPost = lazyWithRetry(() => import('@/pages/BlogPost'));
const Contact = lazyWithRetry(() => import('@/pages/Contact'));
const IISAdmission = lazyWithRetry(() => import('@/pages/IISAdmission'));
const FAQ = lazyWithRetry(() => import('@/pages/FAQ'));
const Help = lazyWithRetry(() => import('@/pages/Help'));
const HelpCenterHub = lazyWithRetry(() => import('@/pages/HelpCenterHub'));
const Accessibility = lazyWithRetry(() => import('@/pages/Accessibility'));
const GenderEqualityPlan = lazyWithRetry(() => import('@/pages/GenderEqualityPlan'));
const TrainingSandboxLanding = lazyWithRetry(() => import('@/pages/training/TrainingSandboxLanding'));
const HipaaNotice = lazyWithRetry(() => import('@/pages/HipaaNotice'));
const Mission = lazyWithRetry(() => import('@/pages/Mission'));
const News = lazyWithRetry(() => import('@/pages/News'));
const NewsArticle = lazyWithRetry(() => import('@/pages/NewsArticle'));
const Careers = lazyWithRetry(() => import('@/pages/Careers'));
const Guides = lazyWithRetry(() => import('@/pages/Guides'));
const GuideDetail = lazyWithRetry(() => import('@/pages/GuideDetail'));
const Research = lazyWithRetry(() => import('@/pages/Research'));
const Partnerships = lazyWithRetry(() => import('@/pages/Partnerships'));
const PatientAdvocacy = lazyWithRetry(() => import('@/pages/PatientAdvocacy'));
const TechnicalSupport = lazyWithRetry(() => import('@/pages/TechnicalSupport'));

const InstallPWA = lazyWithRetry(() => import('@/pages/InstallPWA'));
const PrivacyPolicy = lazyWithRetry(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('@/pages/TermsOfService'));
const UserManual = lazyWithRetry(() => import('@/pages/UserManual'));
const ECOAUserManual = lazyWithRetry(() => import('@/pages/ECOAUserManual'));
const Support = lazyWithRetry(() => import('@/pages/Support'));
const SOPs = lazyWithRetry(() => import('@/pages/SOPs'));
const Unsubscribe = lazyWithRetry(() => import('@/pages/Unsubscribe'));
const Welcome = lazyWithRetry(() => import('@/pages/Welcome'));
const Pricing = lazyWithRetry(() => import('@/pages/Pricing'));
const ShareRedirect = lazyWithRetry(() => import('@/pages/ShareRedirect'));
const ThemeDebug = lazyWithRetry(() => import('@/pages/ThemeDebug'));
const TrustCenter = lazyWithRetry(() => import('@/pages/TrustCenter'));
const GovlinkCertificateVerify = lazyWithRetry(() => import('@/pages/gov/GovlinkCertificateVerify'));
const VerifyCertificate = lazyWithRetry(() => import('@/pages/VerifyCertificate'));
const ClaimComplete = lazyWithRetry(() => import('@/pages/ClaimComplete'));
const TrustSubmissionGenerator = lazyWithRetry(() => import('@/pages/TrustSubmissionGenerator'));
const DataResidency = lazyWithRetry(() => import('@/pages/DataResidency'));
const PartnerDataForm = lazyWithRetry(() => import('@/pages/PartnerDataForm'));

// Observer Portal - Public token-based access for caregivers
const ObserverPortal = lazyWithRetry(() => import('@/pages/ObserverPortal'));

// Landing pages
const PatientsLanding = lazyWithRetry(() => import('@/pages/PatientsLanding'));
const MedicalProfessionalsLanding = lazyWithRetry(() => import('@/pages/MedicalProfessionalsLanding'));
const TrialSitesLanding = lazyWithRetry(() => import('@/pages/TrialSitesLanding'));
const SponsorsLanding = lazyWithRetry(() => import('@/pages/SponsorsLanding'));
const SponsorHub = lazyWithRetry(() => import('@/pages/sponsors/SponsorHub'));
const SponsorArchitecture = lazyWithRetry(() => import('@/pages/sponsors/SponsorArchitecture'));
const SponsorVerifiedFile = lazyWithRetry(() => import('@/pages/sponsors/SponsorVerifiedFile'));
const SponsorVerifiedFileRequestReceived = lazyWithRetry(() => import('@/pages/sponsors/SponsorVerifiedFileRequestReceived'));
const SponsorEvidence = lazyWithRetry(() => import('@/pages/sponsors/SponsorEvidence'));
const SponsorInvestigatorVoice = lazyWithRetry(() => import('@/pages/sponsors/SponsorInvestigatorVoice'));
const SponsorScopingSprint = lazyWithRetry(() => import('@/pages/sponsors/SponsorScopingSprint'));
const SponsorContact = lazyWithRetry(() => import('@/pages/sponsors/SponsorContact'));
const CROLanding = lazyWithRetry(() => import('@/pages/CROLanding'));
const BlueprintSuccessPage = lazyWithRetry(() => import('@/pages/BlueprintSuccessPage'));
const Services = lazyWithRetry(() => import('@/pages/Services'));
const Features = lazyWithRetry(() => import('@/pages/Features'));
const OfflineFirst = lazyWithRetry(() => import('@/pages/OfflineFirst'));
const SponsorSuccess = lazyWithRetry(() => import('@/pages/SponsorSuccess'));
const Bridge = lazyWithRetry(() => import('@/pages/Bridge'));
const BridgeSovereignty = lazyWithRetry(() => import('@/pages/BridgeSovereignty'));
const BridgeFeasibility = lazyWithRetry(() => import('@/pages/BridgeFeasibility'));
const UmojaReg = lazyWithRetry(() => import('@/pages/UmojaReg'));
const WhitepaperVillageToVerified = lazyWithRetry(() => import('@/pages/WhitepaperVillageToVerified'));
const Assessments = lazyWithRetry(() => import('@/pages/Assessments'));
const BlueCloudX = lazyWithRetry(() => import('@/pages/BlueCloudX'));
const InspectionReadiness = lazyWithRetry(() => import('@/pages/InspectionReadiness'));
const ECOALanding = lazyWithRetry(() => import('@/pages/ECOALanding'));
const MedicalImagingLanding = lazyWithRetry(() => import('@/pages/MedicalImagingLanding'));
const EmergencySOSLanding = lazyWithRetry(() => import('@/pages/EmergencySOSLanding'));
const VeevaETMFLanding = lazyWithRetry(() => import('@/pages/VeevaETMFLanding'));
const RFIResponseLanding = lazyWithRetry(() => import('@/pages/RFIResponseLanding'));
const DentalTrialSitesLanding = lazyWithRetry(() => import('@/pages/DentalTrialSitesLanding'));
const MedicalEquipmentTrialsLanding = lazyWithRetry(() => import('@/pages/MedicalEquipmentTrialsLanding'));
const PHCLandingPage = lazyWithRetry(() => import('@/pages/PHCLanding'));
const BridgeSuiteOverview = lazyWithRetry(() => import('@/pages/resources/BridgeSuiteOverview'));
const WhatIsSitelink = lazyWithRetry(() => import('@/pages/resources/WhatIsSitelink'));
const WhatIsSiteCommand = lazyWithRetry(() => import('@/pages/resources/WhatIsSiteCommand'));
const WhatIsCDMS = lazyWithRetry(() => import('@/pages/resources/WhatIsCDMS'));
const AIClinicalDecisionSupport = lazyWithRetry(() => import('@/pages/resources/AIClinicalDecisionSupport'));

const FreeSiteAssessmentLanding = lazyWithRetry(() => import('@/pages/FreeSiteAssessmentLanding'));
const ForSitesFeasibilityReady = lazyWithRetry(() => import('@/pages/ForSitesFeasibilityReady'));

// Demo pages
const MedicalProfessionalsDemo = lazyWithRetry(() => import('@/pages/MedicalProfessionalsDemo'));
const SponsorsDemo = lazyWithRetry(() => import('@/pages/SponsorsDemo'));
const AdvocacyGroupsDemo = lazyWithRetry(() => import('@/pages/AdvocacyGroupsDemo'));
const DemoBooking = lazyWithRetry(() => import('@/pages/DemoBooking'));
const CROServices = lazyWithRetry(() => import('@/pages/CROServices'));
const SMOServices = lazyWithRetry(() => import('@/pages/SMOServices'));
const DemoConfirmation = lazyWithRetry(() => import('@/pages/DemoConfirmation'));
const CalendarPublic = lazyWithRetry(() => import('@/pages/CalendarPublic'));
const TrialSitePricing = lazyWithRetry(() => import('@/pages/TrialSitePricing'));
const SMOPricing = lazyWithRetry(() => import('@/pages/pricing/SMOPricing'));
const TrialSiteDirectory = lazyWithRetry(() => import('@/pages/TrialSiteDirectory'));
const SiteBadgePage = lazyWithRetry(() => import('@/pages/SiteBadgePage'));
const FeasibilityShortlist = lazyWithRetry(() => import('@/pages/FeasibilityShortlist'));

// Resources
const Resources = lazyWithRetry(() => import('@/pages/Resources'));
// FDORACalculator and QRCodeAssets are registered in resource.routes.tsx (canonical location)
const CaseStudies = lazyWithRetry(() => import('@/pages/resources/CaseStudies'));


const EDCTP3HandHygieneOnePager = lazyWithRetry(() => import('@/pages/resources/EDCTP3HandHygieneOnePager'));
const CTMSPillar = lazyWithRetry(() => import('@/pages/CTMSPillar'));
const CTMSCredentials = lazyWithRetry(() => import('@/pages/CTMSCredentials'));
const CTMSVsIndex = lazyWithRetry(() => import('@/pages/CTMSVsIndex'));
const CTMSVsPillar = lazyWithRetry(() => import('@/pages/CTMSVsPillar'));
const CTMSROICalculator = lazyWithRetry(() => import('@/pages/resources/CTMSROICalculator'));
const EditorialTeam = lazyWithRetry(() => import('@/pages/EditorialTeam'));
const EditorialProfile = lazyWithRetry(() =>
  import('@/pages/EditorialTeam').then((m) => ({ default: m.EditorialProfile }))
);

// Directories
const Directory = lazyWithRetry(() => import('@/pages/Directory'));
const DirectoryListingDetail = lazyWithRetry(() => import('@/pages/DirectoryListingDetail'));
const AdvocacyDirectory = lazyWithRetry(() => import('@/pages/AdvocacyDirectory'));
const AdvocacyPartnerDetail = lazyWithRetry(() => import('@/pages/AdvocacyPartnerDetail'));
const DirectoryResearchCenters = lazyWithRetry(() => import('@/pages/DirectoryResearchCenters'));
const RareDiseaseDirectory = lazyWithRetry(() => import('@/pages/RareDiseaseDirectory'));
const ResearchCenterDetail = lazyWithRetry(() => import('@/pages/ResearchCenterDetail'));
const CountryResearchCenters = lazyWithRetry(() => import('@/pages/research-centers/CountryResearchCenters'));

// Onboarding
const DoctorOnboarding = lazyWithRetry(() => import('@/pages/doctors/DoctorOnboarding'));
const AdvocacyPartnerApply = lazyWithRetry(() => import('@/pages/AdvocacyPartnerApply'));

// Marketing
const DiversityInclusion = lazyWithRetry(() => import('@/pages/DiversityInclusion'));
const Organizations = lazyWithRetry(() => import('@/pages/Organizations'));
const ExportsBlog = lazyWithRetry(() => import('@/pages/ExportsBlog'));
const RoadmapDashboard = lazyWithRetry(() => import('@/pages/RoadmapDashboard'));
const PatientFunnelLanding = lazyWithRetry(() => import('@/pages/PatientFunnelLanding'));
const FunnelThankYou = lazyWithRetry(() => import('@/pages/FunnelThankYou'));
const ConsentSigning = lazyWithRetry(() => import('@/pages/ConsentSigning'));
const ConsentThankYou = lazyWithRetry(() => import('@/pages/ConsentThankYou'));
const OnboardTrialSite = lazyWithRetry(() => import('@/pages/OnboardTrialSite'));
const OnboardCRO = lazyWithRetry(() => import('@/pages/OnboardCRO'));

// Government dashboard (public, token-gated)
const GovernmentDashboard = lazyWithRetry(() => import('@/pages/GovernmentDashboard'));
const JoinCRO = lazyWithRetry(() => import('@/pages/JoinCRO'));
const MyDirectoryClaims = lazyWithRetry(() => import('@/pages/MyDirectoryClaims'));



/**
 * Public routes accessible without authentication
 */
export const publicRoutes: RouteObject[] = [
  { path: '/iis-group/admission', element: <IISAdmission /> },
  { path: '/join/:code', element: <JoinCRO /> },
  { path: '/my-claims', element: <MyDirectoryClaims /> },
  { path: '/claim', element: <ClaimComplete /> },
  { path: '/claim/complete', element: <ClaimComplete /> },
  { path: '/', element: <SmartRedirect /> },
  { path: '/home', element: <Index /> },
  { path: '/welcome', element: <Welcome /> },
  { path: '/unsubscribe', element: <Unsubscribe /> },
  { path: '/debug/theme', element: <ThemeDebug /> },

  // Human fallback for share URLs (bots use the static /share/* HTML generated at build)
  { path: '/share/:type/:slug', element: <ShareRedirect /> },
  
  // Company pages
  { path: '/about', element: <About /> },
  { path: '/trust', element: <TrustCenter /> },
  { path: '/trust-center', element: <Navigate to="/trust" replace /> },
  { path: '/trust/submissions', element: <TrustSubmissionGenerator /> },
  { path: '/trust/data-residency', element: <DataResidency /> },
  { path: '/partner-data-form', element: <PartnerDataForm /> },
  { path: '/verify/govlink-certificate', element: <GovlinkCertificateVerify /> },
  { path: '/verify/:certId', element: <VerifyCertificate /> },
  { path: '/mission', element: <Mission /> },
  { path: '/news', element: <News /> },
  { path: '/news/:slug', element: <NewsArticle /> },
  { path: '/careers', element: <Careers /> },
  
  { path: '/partnerships', element: <Partnerships /> },
  { path: '/diversity-inclusion', element: <DiversityInclusion /> },
  { path: '/organizations', element: <Organizations /> },
  { path: '/organizations/:id/settings', element: <OrganizationSettings /> },
  { path: '/organizations/invite/:token', element: <AcceptOrganizationInvite /> },
  { path: '/roadmap', element: <RoadmapDashboard /> },
  
  // Blog
  { path: '/blog', element: <Blog /> },
  { path: '/blog/category/:category', element: <BlogCategory /> },
  { path: '/blog/:slug', element: <BlogPost /> },
  { path: '/exports/blog', element: <ExportsBlog /> },
  
  // Contact & Support
  { path: '/contact', element: <Contact /> },
  { path: '/support', element: <Support /> },
  { path: '/faq', element: <FAQ /> },
  { path: '/help', element: <Help /> },
  { path: '/help-center', element: <HelpCenterHub /> },
  { path: '/technical-support', element: <TechnicalSupport /> },
  
  // Legal
  { path: '/privacy-policy', element: <PrivacyPolicy /> },
  { path: '/terms-of-service', element: <TermsOfService /> },
  { path: '/hipaa-notice', element: <HipaaNotice /> },
  { path: '/accessibility', element: <Accessibility /> },
  { path: '/gender-equality-plan', element: <GenderEqualityPlan /> },
  { path: '/training/sandbox', element: <TrainingSandboxLanding /> },
  
  // Documentation
  { path: '/user-manual', element: <UserManual /> },
  { path: '/ecoa-manual', element: <ECOAUserManual /> },
  { path: '/sops', element: <SOPs /> },
  { path: '/guides', element: <Guides /> },
  { path: '/guides/:slug', element: <GuideDetail /> },
  
  // Resources
  { path: '/resources', element: <Resources /> },
  { path: '/resources/case-studies', element: <CaseStudies /> },

  { path: '/resources/edctp3-hand-hygiene', element: <EDCTP3HandHygieneOnePager /> },
  { path: '/research', element: <Research /> },
  
  // Landing pages by audience
  { path: '/patients', element: <PatientsLanding /> },
  { path: '/medical-professionals', element: <MedicalProfessionalsLanding /> },
  { path: '/trial-sites', element: <TrialSitesLanding /> },
  { path: '/trial-sites/success', element: <BlueprintSuccessPage /> },
  { path: '/sponsors', element: <SponsorsLanding /> },
  { path: '/cro', element: <CROLanding /> },
  { path: '/patient-advocacy', element: <PatientAdvocacy /> },
  { path: '/services', element: <Services /> },
  { path: '/features', element: <Features /> },
  { path: '/sponsor-success', element: <SponsorSuccess /> },
  { path: '/pricing', element: <Pricing /> },
  { path: '/pricing/trial-sites', element: <TrialSitePricing /> },
  { path: '/pricing/smo', element: <SMOPricing /> },
  { path: '/trial-site-directory', element: <TrialSiteDirectory /> },
  { path: '/site-badge/:assessmentId', element: <SiteBadgePage /> },
  { path: '/feasibility-shortlist', element: <FeasibilityShortlist /> },
  { path: '/bridge', element: <Bridge /> },
  { path: '/bridge-sovereignty', element: <BridgeSovereignty /> },
  { path: '/bridge/feasibility', element: <BridgeFeasibility /> },
  { path: '/for-sponsors/feasibility', element: <BridgeFeasibility /> },
  { path: '/for-sites/feasibility-ready', element: <ForSitesFeasibilityReady /> },
  { path: '/for-investigators', element: <ForSitesFeasibilityReady /> },

  // Sponsor funnel — one page per rung of the offer ladder
  { path: '/for-sponsors', element: <SponsorHub /> },
  { path: '/for-sponsors/architecture', element: <SponsorArchitecture /> },
  { path: '/for-sponsors/verified-file', element: <SponsorVerifiedFile /> },
  { path: '/for-sponsors/verified-file/request-received', element: <SponsorVerifiedFileRequestReceived /> },
  { path: '/for-sponsors/evidence', element: <SponsorEvidence /> },
  { path: '/for-sponsors/investigator-voice', element: <SponsorInvestigatorVoice /> },
  { path: '/for-sponsors/scoping-sprint', element: <SponsorScopingSprint /> },
  { path: '/for-sponsors/contact', element: <SponsorContact /> },
  { path: '/umojareg', element: <UmojaReg /> },
  { path: '/whitepaper/village-to-verified', element: <WhitepaperVillageToVerified /> },
  { path: '/offline-first', element: <OfflineFirst /> },
  { path: '/assessments', element: <Assessments /> },
  { path: '/bluecloudx', element: <BlueCloudX /> },
  { path: '/inspection-readiness', element: <InspectionReadiness /> },
  { path: '/ecoa', element: <ECOALanding /> },
  { path: '/medical-imaging', element: <MedicalImagingLanding /> },
  { path: '/emergency-sos', element: <EmergencySOSLanding /> },
  { path: '/veeva-etmf', element: <VeevaETMFLanding /> },
  { path: '/rfi-response', element: <RFIResponseLanding /> },
  { path: '/dental-trial-sites', element: <DentalTrialSitesLanding /> },
  { path: '/medical-equipment-trials', element: <MedicalEquipmentTrialsLanding /> },
  { path: '/why-bridge', element: <Navigate to="/bridge" replace /> },
  { path: '/solutions/phc', element: <PHCLandingPage /> },
  { path: '/solutions/bridge-suite', element: <BridgeSuiteOverview /> },
  { path: '/solutions/sitelink', element: <WhatIsSitelink /> },
  { path: '/solutions/sitecommand', element: <WhatIsSiteCommand /> },
  { path: '/solutions/cdms', element: <WhatIsCDMS /> },
  { path: '/solutions/ai-cdst', element: <AIClinicalDecisionSupport /> },
  { path: '/free-site-assessment', element: <FreeSiteAssessmentLanding /> },
  
  // Demo pages
  { path: '/demo/professionals', element: <MedicalProfessionalsDemo /> },
  { path: '/demo/sponsors', element: <SponsorsDemo /> },
  { path: '/demo/advocacy', element: <AdvocacyGroupsDemo /> },
  { path: '/demo-booking', element: <DemoBooking /> },
  { path: '/cro-services', element: <CROServices /> },
  { path: '/smo-services', element: <SMOServices /> },
  { path: '/demo-confirmation', element: <DemoConfirmation /> },
  { path: '/calendar-public', element: <CalendarPublic /> },
  
  // Directories
  { path: '/directory', element: <Directory /> },
  { path: '/directory/listing/:id', element: <DirectoryListingDetail /> },
  { path: '/directory/advocacy', element: <AdvocacyDirectory /> },
  { path: '/directory/advocacy/:id', element: <AdvocacyPartnerDetail /> },
  { path: '/directory/rare-disease', element: <RareDiseaseDirectory /> },
  { path: '/directory/research-centers', element: <DirectoryResearchCenters /> },
  { path: '/directory/research-centers/:id', element: <ResearchCenterDetail /> },
  { path: '/research-centers/:country', element: <CountryResearchCenters /> },
  
  // Onboarding
  { path: '/doctors/onboard', element: <DoctorOnboarding /> },
  { path: '/advocacy/apply', element: <AdvocacyPartnerApply /> },
  
  // Funnels
  { path: '/patient-funnel', element: <PatientFunnelLanding /> },
  { path: '/patient-funnel/thank-you', element: <FunnelThankYou /> },
  
  // eConsent
  { path: '/consent/:id', element: <ConsentSigning /> },
  { path: '/consent/thank-you', element: <ConsentThankYou /> },
  
  // Trial Site Onboarding
  { path: '/onboard-trial-site', element: <OnboardTrialSite /> },
  
  // CRO Onboarding
  { path: '/onboard-cro', element: <OnboardCRO /> },
  
  // Observer Portal - Public token-based access for caregivers/observers
  { path: '/observer/:token', element: <ObserverPortal /> },
  
  // Government Dashboard - Public token-based access for MoH/regulators
  { path: '/gov/dashboard/:token', element: <GovernmentDashboard /> },
  { path: '/gov/surveillance/:token', element: <GovernmentDashboard /> },
  
  // /gov/who-gbt-dashboard -> umojareg.org/umoja/gbt/dashboard (renderUmojaregRedirects)


  // Legacy redirects for embedded regulatory modules
  { path: '/ethics-track', element: <Navigate to="/site-dashboard" replace /> },
  { path: '/twinnet', element: <Navigate to="/site-dashboard" replace /> },

  // Other
  { path: '/install', element: <InstallPWA /> },

  // CTMS pillar (SEO outrank cluster — global, africa, country pages)
  { path: '/ctms', element: <CTMSPillar /> },
  { path: '/ctms/credentials', element: <CTMSCredentials /> },
  { path: '/ctms/:country', element: <CTMSPillar /> },

  // CTMS comparison pages (Track 2 — BoFu outrank cluster)
  { path: '/vs', element: <CTMSVsIndex /> },
  { path: '/vs/:competitor', element: <CTMSVsPillar /> },
  { path: '/resources/ctms-roi-calculator', element: <CTMSROICalculator /> },

  // Editorial team (E-E-A-T author + reviewer profiles)
  { path: '/about/editorial-team', element: <EditorialTeam /> },
  { path: '/about/editorial-team/:slug', element: <EditorialProfile /> },
];

// Export lazy components for prefetching
export const publicRoutePrefetch = {
  Index,
  About,
  Blog,
  Contact,
  PatientsLanding,
  MedicalProfessionalsLanding,
  TrialSitesLanding,
  SponsorsLanding,
};
